/**
 * The response side of the machine-readable API contract (published by ./describe.ts in `system.describe` and GET
 * /api/schema), next to the request payload schemas that ./validators.ts generates:
 *  - RESULT_SCHEMAS: the JSON Schema of the `result` of every request type;
 *  - EVENT_PAYLOAD_SCHEMAS: the JSON Schema of the `payload` of every server event;
 *  - GAME_STATE_FORMATS: the stored game state document (`fwp-state-v1`: the `gameState` of profile.load and of every
 *    player.command reply) with its value encoding;
 *  - COMMAND_CATALOGUE (./commands.ts): every player.command op with its args, result and refusal reasons.
 * Hand-written next to the TypeScript types of ./model.ts and ./messages.ts; tests/backend/protocol-contract.test.ts
 * checks them against real replies and events (every object field listed: the test closes the objects) and against
 * the rule fixtures.
 *
 * Compatibility rule for clients of every platform: objects may gain fields in later server versions (ignore unknown
 * fields); a field is never removed or retyped within a protocol version. Only the JSON Schema keywords the payload
 * schemas use appear here (no $ref: shared parts are inlined).
 */
import { ALL_FEATURES, EVENT_TYPES, type EventType, PAGINATED_REQUESTS, type RequestType } from './messages';
import { BATTLE_MODES, CHAT_CHANNEL_KINDS, CHAT_DELETE_REASONS, CHAT_SEND_BLOCKS, CLAN_EVENT_TYPES, CLAN_JOIN_POLICIES, CLAN_ROLES, LEADERBOARD_BOARDS } from './model';
import { ONLINE_MAPS, ONLINE_MAP_IDS } from '../../session/online';
import { type JsonSchema, toJsonSchema } from './schema';
import { COMMAND_LOG_SCHEMA, INPUT_COMMAND_SCHEMA, battleInitValidator, roomSettingsValidator } from './validators';

type S = JsonSchema;

// ------------------------------------------------------------------------------------------------ building blocks

export const str: S = { type: 'string' };
export const int: S = { type: 'integer' };
export const num: S = { type: 'number' };
export const bool: S = { type: 'boolean' };
/** Epoch milliseconds (server time). */
export const ts: S = { type: 'integer', description: 'epoch ms' };
export const anyJson: S = { description: 'any JSON value' };
/** An object whose fields are not listed here (opaque or documented elsewhere). */
export const openObject = (description: string): S => ({ type: 'object', additionalProperties: true, description });
export const nullable = (s: S): S => ({ anyOf: [s, { type: 'null' }] });
export const arrayOf = (items: S): S => ({ type: 'array', items });
export const mapOf = (value: S, description?: string): S => ({ type: 'object', additionalProperties: value, ...(description ? { description } : {}) });
export const oneOfValues = (...values: (string | number | boolean)[]): S => (values.length === 1 ? { const: values[0] } : { enum: values });
export const described = (s: S, description: string): S => ({ ...s, description });

/** An object with these fields; every field is required except `optional` ones. Unknown fields may appear later. */
export function obj(props: Record<string, S>, optional: readonly string[] = [], description?: string): S {
  const required = Object.keys(props).filter((k) => !optional.includes(k));
  const out: S = { type: 'object', properties: props };
  if (required.length > 0) out.required = required;
  if (description) out.description = description;
  return out;
}

const EMPTY: S = obj({}, [], 'no fields');
const cursorNext: S = described(nullable(str), 'opaque cursor of the following page (null = last page); pass it back as `cursor`');

// ------------------------------------------------------------------------------------------------ game state

/** `{"$pi": n}`: a ProtectedInt of the game (an int32). */
const PI_MARKER: S = obj({ $pi: int }, [], 'ProtectedInt: {"$pi": value} (int32)');
/** A value that is either a ProtectedInt marker or (older states) a plain number. */
const PI_VALUE: S = { anyOf: [PI_MARKER, num], description: 'ProtectedInt {"$pi": n} (states the server writes), or a plain number in older states' };
const SPARSE_MARKER: S = obj(
  { $sparse: { type: 'integer', minimum: 0, description: 'array length' }, e: arrayOf({ type: 'array', minItems: 2, maxItems: 2, description: '[index or key, encoded value]' }) },
  [],
  'sparse array: {"$sparse": length, "e": [[index, value], ...]}; missing indexes are holes',
);
const NUMBER_MARKER: S = obj({ $n: oneOfValues('NaN', 'Infinity', '-Infinity') }, [], 'a non-finite number');
const UNDEFINED_MARKER: S = obj({ $u: oneOfValues(1) }, [], 'undefined (a field that exists with no value)');

const WEAPON_COPY: S = {
  ...obj({ range: int, stolen: bool, crafted: bool }, ['range', 'stolen', 'crafted'], 'one weapon copy (more fields may appear); slots past `count` are leftovers, possibly {}'),
  additionalProperties: true,
};
/** A number inside the state: a plain number, or a NaN/undefined marker the game kept. */
const NUM_VALUE: S = { anyOf: [num, NUMBER_MARKER, UNDEFINED_MARKER] };

/** The `player` fields the screens read (the state carries more; see the description). */
const PLAYER_STATE: S = {
  ...obj(
    {
      level: PI_VALUE,
      exp: PI_VALUE,
      gold: PI_VALUE,
      diam: PI_VALUE,
      attack: PI_VALUE,
      protect: PI_VALUE,
      health: PI_VALUE,
      worms: PI_VALUE,
      skill_pts: PI_VALUE,
      perk_pts: PI_VALUE,
      perks: mapOf(NUM_VALUE, 'PERKS_DEF id -> level'),
      weapons: mapOf({ type: 'array', description: '[count, copy 1, ..., copy count]; a copy: {"range", "stolen", "crafted"}', items: { anyOf: [int, WEAPON_COPY] } }, 'WEAPONS_DEF id -> copies'),
      stim_behave: mapOf(NUM_VALUE, 'stimulants owned: id -> count'),
      stim_to_fight: mapOf(NUM_VALUE, 'stimulants taken into the next battle: id -> count'),
      objs: mapOf(NUM_VALUE, 'objects (books, crafting parts, junk): id -> count'),
      flags: mapOf(NUM_VALUE, 'flags owned: id -> 1'),
      rip: mapOf(NUM_VALUE, 'graves owned: id -> 1'),
      impl: mapOf(NUM_VALUE, 'implants owned: id -> count'),
      recipes: { description: 'known recipes: [CRAFT_DEF count, 0/1 per recipe] (array or sparse-array marker)' },
      achiev: mapOf(NUM_VALUE, 'achievement counters'),
      settings: openObject('skill (difficulty, +10 = hardcore), help, sound, exhaust, no_animation'),
      spec: NUM_VALUE,
      career_max: NUM_VALUE,
      missions_max: NUM_VALUE,
      survival_max: NUM_VALUE,
      boss_max: NUM_VALUE,
      tired: NUM_VALUE,
      rating: NUM_VALUE,
      cur_task: NUM_VALUE,
      cur_flag: NUM_VALUE,
      cur_rip: NUM_VALUE,
      cur_impl: NUM_VALUE,
    },
    ['perks', 'weapons', 'stim_behave', 'stim_to_fight', 'objs', 'flags', 'rip', 'impl', 'recipes', 'achiev', 'settings', 'spec', 'career_max', 'missions_max', 'survival_max', 'boss_max', 'tired', 'rating', 'cur_task', 'cur_flag', 'cur_rip', 'cur_impl'],
    'FWP.player as the game keeps it (field names of the original). Listed: the fields clients read for wallets, inventory and progress; every other field is kept as the game wrote it',
  ),
  additionalProperties: true,
};

/** The stored game state document: `PlayerProfile.gameState`, `player.command` `gameState`. */
export const GAME_STATE_SCHEMA: S = {
  ...obj(
    {
      format: oneOfValues('fwp-state-v1'),
      player: PLAYER_STATE,
      map_info: { description: 'explored cells of the world map (array or sparse-array marker); opaque to clients' },
    },
    [],
    'The canonical game state (stateFormats["fwp-state-v1"] describes the value encoding). The server owns it at trust level L2: clients show it and send player.command, they never edit it.',
  ),
  additionalProperties: true,
};

export interface GameStateFormat {
  description: string;
  /** JSON Schema of the document. */
  schema: S;
  /** JSON Schemas of the value markers that may replace any value inside `player` / `map_info`. */
  markers: Record<string, S>;
  /** How to read it without the game code. */
  decoding: string[];
}

export const GAME_STATE_FORMATS: Record<string, GameStateFormat> = {
  'fwp-state-v1': {
    description:
      'The game state document of the original game (FWP.player and FWP.map_info with the field names of the original), serialized so that it round-trips exactly: JSON has no ProtectedInt, sparse arrays, NaN or undefined, so those values are written as markers. A marker is an object with exactly the listed keys.',
    schema: GAME_STATE_SCHEMA,
    markers: { protectedInt: PI_MARKER, sparseArray: SPARSE_MARKER, nonFiniteNumber: NUMBER_MARKER, undefined: UNDEFINED_MARKER },
    decoding: [
      '{"$pi": n} -> the number n (int32): gold, diam, level, exp, attack, protect, health, worms, skill_pts, perk_pts',
      '{"$sparse": length, "e": [[i, v], ...]} -> an array of `length` with v at index i (other indexes empty)',
      '{"$n": "NaN" | "Infinity" | "-Infinity"} -> that number',
      '{"$u": 1} -> no value (treat the field as absent)',
      'decode recursively: a marker may appear wherever a value may',
      'player.weapons[id] = [count, copy 1, ..., copy count]; a copy is {range (rank 1..10), stolen, crafted}; entries past count are leftovers to ignore',
    ],
  },
};

// ------------------------------------------------------------------------------------------------ model

export const DEVICE_INFO: S = obj({ platform: str, model: str, appVersion: str, label: str }, ['model', 'appVersion', 'label']);
export const ACCOUNT_INFO: S = obj({ playerId: str, displayName: str, username: nullable(str), guest: bool, createdAt: ts });

const AUTH_RESULT_PROPS: Record<string, S> = {
  playerId: str,
  displayName: str,
  accessToken: described(str, 'bearer access token (Authorization: Bearer ...)'),
  accessExpiresAt: ts,
  refreshToken: described(str, 'refresh token for auth.refresh (rotated on every use); store it securely'),
  refreshExpiresAt: ts,
  sessionId: described(str, 'public id of the device session (auth.sessions, auth.revoke); not a credential'),
  account: ACCOUNT_INFO,
  serverTime: ts,
  isNew: bool,
  deviceToken: described(str, 'returned once for a new guest device: persist it (auth.guest { deviceToken } signs in again)'),
  username: nullable(str),
  token: described(str, 'deprecated: accessToken'),
  expiresAt: described(ts, 'deprecated: accessExpiresAt'),
};
export const AUTH_RESULT: S = obj(AUTH_RESULT_PROPS, ['refreshToken', 'refreshExpiresAt', 'deviceToken']);
export const ISSUED_AUTH_RESULT: S = obj(AUTH_RESULT_PROPS, ['deviceToken']);
const AUTH_SESSION_INFO: S = obj({ sessionId: str, device: DEVICE_INFO, createdAt: ts, lastSeenAt: ts, current: bool });

const SUMMARY_KEYS = ['level', 'exp', 'rating', 'assaults', 'attack', 'protect', 'health', 'worms', 'spec', 'careerMax', 'missionsMax', 'survivalMax', 'bossMax'];
export const PROFILE_SUMMARY: S = obj(Object.fromEntries(SUMMARY_KEYS.map((k) => [k, int])), [], 'public projection of the game state (leaderboards, public profiles)');

export const PLAYER_CARD: S = obj(
  {
    playerId: described(str, 'stable id of the player'),
    nickname: described(str, 'the nickname as typed at sign-up (case kept); the display name of guest and username accounts'),
    level: int,
    exp: int,
    guest: described(bool, 'a guest account (device token only, no nickname and password)'),
    createdAt: ts,
    summary: PROFILE_SUMMARY,
  },
  ['summary'],
  'the player as the account screens show it; level and exp come from the canonical game state',
);
const SIGNED_IN_RESULT: S = obj({ ...AUTH_RESULT_PROPS, player: PLAYER_CARD }, ['deviceToken']);
const CURRENT_SESSION_INFO: S = obj({
  sessionId: str,
  device: DEVICE_INFO,
  createdAt: ts,
  lastSeenAt: ts,
  accessExpiresAt: described(ts, 'expiry of the access token this request was authorized with'),
  refreshExpiresAt: described(ts, 'expiry of the refresh token (the session ends then unless refreshed)'),
});
const PROFILE_VOLATILE: S = obj({ papaHere: bool, newDay: bool, presentAvailable: bool, present: int, tzOffsetMin: int }, [], 'facts of the session entry that are not part of the game state');
const LEGACY_IMPORT: S = obj({ format: oneOfValues('v45', 'v45-short'), data: str, importedAt: ts });
const PROFILE_GAME_STATE: S = {
  anyOf: [GAME_STATE_SCHEMA, openObject('trust levels L0/L1: the state document the client saved'), { type: 'null' }],
  description: 'fwp-state-v1 (trust level L2, the default); null before the first state',
};
export const PLAYER_PROFILE: S = obj({
  id: str,
  displayName: str,
  createdAt: ts,
  updatedAt: ts,
  revision: int,
  schemaVersion: oneOfValues(1),
  gameState: PROFILE_GAME_STATE,
  summary: PROFILE_SUMMARY,
  legacyImport: nullable(LEGACY_IMPORT),
});
const TEAM_SNAPSHOT: S = obj({
  playerId: str,
  displayName: str,
  level: int,
  attack: int,
  protect: int,
  health: int,
  worms: int,
  spec: int,
  perks: mapOf(int),
  weapons: mapOf(int),
  wormNames: arrayOf(str),
  implant: int,
  flag: int,
  takenAt: ts,
});
const CLAN_ROLE: S = oneOfValues(...CLAN_ROLES);
const CLAN_JOIN_POLICY: S = oneOfValues(...CLAN_JOIN_POLICIES);
export const CLAN_BRIEF: S = obj({ clanId: int, name: str, emblem: described(int, 'frame of the clan_icons sprite (limits.clans.emblems)') }, [], 'a clan as a player or a zone shows it');

const CHAT_CHANNEL: S = described(str, "'global' or 'clan:<clanId>'");
/** A chat message (chat.history items, chat.send, chat.message). */
export const CHAT_MESSAGE: S = obj(
  {
    messageId: str,
    channel: CHAT_CHANNEL,
    seq: described(int, 'position in the channel, strictly increasing (chat.markRead)'),
    at: ts,
    playerId: str,
    nickname: described(str, "the sender's nickname as it is now (a rename changes the kept messages too)"),
    level: described(int, "the sender's level when it wrote the message"),
    clan: described(nullable(CLAN_BRIEF), "the sender's clan when it wrote the message, as the clan is now (null once it is gone)"),
    text: described(str, 'plain text (never markup): sanitized, filter words masked with *'),
  },
  [],
  'a chat message',
);
const CHAT_CHANNEL_INFO: S = obj({
  channel: CHAT_CHANNEL,
  kind: oneOfValues(...CHAT_CHANNEL_KINDS),
  clan: nullable(CLAN_BRIEF),
  canSend: bool,
  sendBlock: nullable(oneOfValues(...CHAT_SEND_BLOCKS)),
  lastSeq: int,
  readSeq: int,
  unread: int,
  subscribed: bool,
});
const PUBLIC_PROFILE: S = obj({
  playerId: str,
  displayName: str,
  createdAt: ts,
  updatedAt: ts,
  clanId: nullable(int),
  clan: nullable(CLAN_BRIEF),
  badges: arrayOf(str),
  summary: PROFILE_SUMMARY,
  team: nullable(TEAM_SNAPSHOT),
});
const GRANT: S = obj(
  {
    grantId: str,
    playerId: str,
    createdAt: ts,
    source: oneOfValues('auction_win', 'auction_refund', 'auction_sale', 'operator'),
    kind: oneOfValues('item', 'currency'),
    itemKind: str,
    itemId: str,
    quantity: int,
    currency: oneOfValues('diam', 'gold'),
    amount: int,
    note: str,
    refId: str,
  },
  ['itemKind', 'itemId', 'quantity', 'currency', 'amount', 'note', 'refId'],
);

const LEADERBOARD_ENTRY: S = obj({ rank: int, playerId: str, displayName: str, level: int, exp: int, rating: int, assaults: int, spec: int, clanId: nullable(int), clan: nullable(CLAN_BRIEF) });
const LEADERBOARD_PAGE: S = obj({
  board: oneOfValues(...LEADERBOARD_BOARDS),
  page: int,
  pageSize: int,
  totalPages: int,
  totalEntries: int,
  entries: arrayOf(LEADERBOARD_ENTRY),
  me: nullable(LEADERBOARD_ENTRY),
  items: arrayOf(LEADERBOARD_ENTRY),
  next: cursorNext,
});

const ASSAULT_ZONE: S = obj({
  zone: int,
  stage: int,
  ownerId: nullable(str),
  ownerName: nullable(str),
  ownerClanId: described(nullable(int), "the owner's clan now (a clan holds the zones of its current members)"),
  ownerClan: nullable(CLAN_BRIEF),
  lastCaptureAt: ts,
});
const ASSAULT_TARGET: S = obj({ zone: ASSAULT_ZONE, defender: nullable(TEAM_SNAPSHOT), canAttack: bool, blockedReason: nullable(oneOfValues('own', 'clan')), tributePerHour: int });
const TRIBUTE_RESULT: S = obj({
  myGold: int,
  clanGold: int,
  total: int,
  nextPaymentAt: ts,
  serverTime: ts,
  zones: arrayOf(obj({ zone: int, gold: int, share: oneOfValues('own', 'clan') })),
});
const CLAN_SUMMARY_PROPS: Record<string, S> = {
  clanId: int,
  name: str,
  rating: described(int, 'the captures of the current members'),
  memberCount: int,
  emblem: int,
  joinPolicy: CLAN_JOIN_POLICY,
  zoneCount: described(int, 'zones the members hold now'),
  maxMembers: int,
};
const CLAN_SUMMARY: S = obj(CLAN_SUMMARY_PROPS);
const CLAN_ZONE: S = obj({ zone: int, ownerId: str, ownerName: str, stage: int, lastCaptureAt: ts, tributePerHour: int });
export const CLAN_INFO: S = obj({ ...CLAN_SUMMARY_PROPS, description: str, leaderId: str, leaderName: str, createdAt: ts, zones: arrayOf(CLAN_ZONE) }, [], 'the clan card');
const CLAN_MEMBER: S = obj({ playerId: str, displayName: str, level: int, captures: int, role: CLAN_ROLE, joinedAt: ts });
const CLAN_JOIN_REQUEST: S = obj({ clanId: int, clanName: str, playerId: str, displayName: str, level: int, at: ts });
const CLAN_EVENT: S = obj(
  {
    seq: int,
    at: ts,
    type: oneOfValues(...CLAN_EVENT_TYPES),
    actorId: nullable(str),
    actorName: nullable(str),
    targetId: nullable(str),
    targetName: nullable(str),
    zone: int,
    stage: int,
    zones: arrayOf(int),
    role: CLAN_ROLE,
    from: str,
    to: str,
    emblem: int,
    joinPolicy: CLAN_JOIN_POLICY,
    description: bool,
    via: CLAN_JOIN_POLICY,
    byId: nullable(str),
    byName: nullable(str),
    byClanId: nullable(int),
    byClanName: nullable(str),
    action: oneOfValues('rename', 'description', 'remove', 'leader'),
    reason: oneOfValues('account_deleted', 'moderated'),
  },
  ['zone', 'stage', 'zones', 'role', 'from', 'to', 'emblem', 'joinPolicy', 'description', 'via', 'byId', 'byName', 'byClanId', 'byClanName', 'action', 'reason'],
  'one event of a clan history; the fields of each type: ClanEvent in src/shared/protocol/model.ts (docs/api.md)',
);
const CLAN_MINE: S = obj({
  clan: nullable(CLAN_INFO),
  role: nullable(CLAN_ROLE),
  requests: arrayOf(CLAN_JOIN_REQUEST),
  pendingRequests: int,
  rejoinAt: nullable(ts),
  canCreate: obj({ ok: bool, reason: str, at: ts }, ['reason', 'at']),
});

const AUCTION_STATE_PROPS: Record<string, S> = {
  itemId: str,
  price: described(int, 'diamonds of the next purchase'),
  startPrice: int,
  holder: nullable(obj({ playerId: str, displayName: str })),
  since: nullable(ts),
  endsAt: nullable(ts),
  round: int,
  roundMs: int,
  purchases: int,
  winners: arrayOf(obj({ playerId: str, displayName: str, wonAt: ts, price: int })),
  winnerCount: int,
};
export const AUCTION_STATE: S = obj(AUCTION_STATE_PROPS);
export const AUCTION_HOLDINGS: S = obj({ held: int, permanent: int, total: int });
const AUCTION_INFO: S = obj({ ...AUCTION_STATE_PROPS, mine: AUCTION_HOLDINGS, serverTime: ts });
const NEWS_ITEM: S = obj({ newsId: str, title: str, body: str, publishedAt: ts });

const BATTLE_RESULT: S = obj({ winnerTeam: int });
/**
 * The map of a room or an online battle as the server sends it: always both fields, naming the same PvP map (the
 * schemas of the request's fields, so the API description names them alike: OnlineMapId).
 */
const MAP_ID: S = oneOfValues(...ONLINE_MAP_IDS);
const MAP_INDEX: S = { type: 'integer', minimum: 1, maximum: ONLINE_MAPS.length };
/** RoomSettings of a room: both map fields (a request may name the map by one of them: roomSettingsValidator). */
const ROOM_SETTINGS: S = obj(
  { mapId: MAP_ID, mapIndex: MAP_INDEX, teams: (toJsonSchema(roomSettingsValidator).properties as Record<string, S>).teams },
  [],
  'at most 12 worms in total',
);
const BATTLE_INIT: S = toJsonSchema(battleInitValidator);
const ROOM_PLAYER: S = obj({ playerId: str, displayName: str, team: int, ready: bool, connected: bool, disconnectedAt: nullable(ts) });
export const ROOM: S = obj({
  roomId: str,
  code: described(nullable(str), 'join code of a private room'),
  hostId: str,
  mode: oneOfValues('just'),
  private: bool,
  settings: ROOM_SETTINGS,
  players: arrayOf(ROOM_PLAYER),
  maxPlayers: int,
  state: oneOfValues('lobby', 'in_game', 'closed'),
  sessionId: nullable(str),
  createdAt: ts,
  updatedAt: ts,
  version: int,
});
const MATCH_TICKET: S = obj({ ticketId: str, mode: oneOfValues('just'), teams: int, wormsPerTeam: int, levelBucket: int, queuedAt: ts });
const SESSION_PARTICIPANT: S = obj({ playerId: str, displayName: str, teams: arrayOf(int), connected: bool, disconnectedAt: nullable(ts), finished: bool, forfeited: bool });
const VERDICT: S = oneOfValues('verified', 'rejected', 'unverified');
export const SESSION_OUTCOME: S = obj(
  {
    result: BATTLE_RESULT,
    verified: bool,
    verdict: VERDICT,
    reason: described(nullable(str), 'why the verdict (human-readable; validator_disabled / no_game_assets without a battle validator)'),
    consensus: bool,
    replayId: nullable(str),
    endedAt: ts,
    diverged: arrayOf(str),
  },
  ['diverged'],
);
const ONLINE_BATTLE_INIT: S = obj({
  kind: oneOfValues('online'),
  mode: oneOfValues('just'),
  mapId: MAP_ID,
  mapIndex: MAP_INDEX,
  teams: arrayOf(obj({ team: int, name: str, color: int, worms: int, cpu_level: int, playerId: nullable(str) })),
  turnTimeSec: int,
  neutral: oneOfValues(true),
});
export const SESSION_INFO: S = obj({
  sessionId: str,
  mode: oneOfValues(...BATTLE_MODES),
  stage: int,
  roomId: nullable(str),
  seed: int,
  turnTimeSec: int,
  init: BATTLE_INIT,
  online: nullable(ONLINE_BATTLE_INIT),
  participants: arrayOf(SESSION_PARTICIPANT),
  teamOrder: arrayOf(int),
  state: oneOfValues('active', 'ended'),
  turn: int,
  currentTeam: int,
  turnStartTick: int,
  inputClosedAt: nullable(int),
  deadlineAt: nullable(ts),
  createdAt: ts,
  outcome: nullable(SESSION_OUTCOME),
  version: int,
});
const SESSION_TURN_SUMMARY: S = obj({ turn: int, team: int, startTick: int, endTick: nullable(int), throughTick: int, forcedAt: nullable(int), commands: int });
const SESSION_CHECKPOINT: S = obj({ tick: int, hash: str });
const REPLAY_SUMMARY_PROPS: Record<string, S> = {
  replayId: str,
  playerId: str,
  createdAt: ts,
  mode: oneOfValues(...BATTLE_MODES),
  stage: int,
  result: BATTLE_RESULT,
  verified: bool,
  sessionId: nullable(str),
  verdict: VERDICT,
  battleId: str,
};
const REPLAY_SUMMARY: S = obj(REPLAY_SUMMARY_PROPS, ['verdict', 'battleId']);
const REPLAY_RECORD: S = obj(
  { ...REPLAY_SUMMARY_PROPS, seed: nullable(int), init: nullable(BATTLE_INIT), log: nullable(COMMAND_LOG_SCHEMA), legacy: nullable(str), logHash: nullable(str) },
  ['verdict', 'battleId'],
);

const SERVER_LIMITS: S = obj({
  maxRequestBytes: int,
  maxGameStateBytes: int,
  leaderboardPageSize: int,
  leaderboardMaxPages: int,
  maxInputBatch: int,
  pageSizes: obj(Object.fromEntries(PAGINATED_REQUESTS.map((t) => [t, obj({ default: int, max: int })]))),
  rateLimit: nullable(obj({ burst: int, perSecond: num, accountsPerIpBurst: int, accountsPerIpPerHour: num })),
  eventReplay: obj({ maxEvents: int, retentionMs: int }),
  maxPollWaitMs: int,
  clans: obj(
    {
      nameMin: int,
      nameMax: int,
      descriptionMax: int,
      maxMembers: int,
      maxOfficers: int,
      emblems: arrayOf(int),
      minLevelToCreate: int,
      createCooldownMs: int,
      renameCooldownMs: int,
      rejoinCooldownMs: int,
      maxRequestsPerPlayer: int,
      maxRequestsPerClan: int,
    },
    [],
    'the rules of the player-made clans',
  ),
  chat: obj(
    {
      textMax: int,
      historyGlobal: int,
      historyClan: int,
      minLevelToSend: int,
      guestsCanSend: bool,
      burst: int,
      windowMs: int,
      minIntervalMs: int,
      duplicateWindowMs: int,
    },
    [],
    'the rules of the chat (src/shared/chat-rules.ts)',
  ),
});

/** A server event as it arrives (the payload per event: EVENT_PAYLOAD_SCHEMAS). */
const EVENT_ENVELOPE: S = obj({ v: int, event: oneOfValues(...EVENT_TYPES), payload: anyJson, seq: int, ts }, [], 'a server event (payload: events[].payload of this description)');
const EVENT_BATCH: S = obj({
  events: arrayOf(EVENT_ENVELOPE),
  last: described(int, "the player's newest event seq: resume after it"),
  gap: described(bool, 'events after `after` are no longer available: reload the state and continue after `last`'),
});

export const PLAYER_COMMAND_RESULT: S = obj(
  {
    revision: int,
    gameState: GAME_STATE_SCHEMA,
    result: { description: "the op's result: commands[op].result of this description" },
    serverTime: ts,
    volatile: mapOf(anyJson, 'op-specific facts that are not part of the state (e.g. tzOffsetMin of settings.set)'),
  },
  ['volatile'],
);

// ------------------------------------------------------------------------------------------------ results

const roomResult = obj({ room: ROOM });
const sessionResult = obj({ session: SESSION_INFO });

/** JSON Schema of the `result` of every request type. */
// ------------------------------------------------------------------------------------------------ store and ads

const ITEM_COUNTS: S = mapOf(int, 'stimulator id (STIM_DEF) -> count');
const STORE_PRODUCT: S = obj({
  productId: described(str, "the store's product id (Google Play Console)"),
  kind: oneOfValues('currency_pack', 'bundle'),
  gold: int,
  diam: int,
  items: ITEM_COUNTS,
  multiplier: described(int, 'the next purchase is credited this many times (first purchase x2); 1 = no bonus'),
  limitPerAccount: described(nullable(int), 'purchases per account (null = unlimited)'),
  purchased: described(int, 'purchases of this account'),
  available: described(bool, 'the player may buy it now'),
  shop: described(nullable(obj({ tab: oneOfValues('diamonds', 'gold', 'offer'), slot: int })), 'where the in-game shop shows it (null = not in the game)'),
  label: nullable(oneOfValues('popular', 'best_value')),
  availableUntil: described(nullable(ts), "end of the offer's current window"),
  listPrices: described(mapOf(num, 'ISO 4217 currency code -> price'), "the owner's price list (USD and UAH always): shown where the platform store reports no price"),
  bonusPct: described(int, "the pack's bonus over the base rate in percent (the shop's badge; 0 = none)"),
});
export const STORE_CATALOG: S = obj({
  providers: described(arrayOf(oneOfValues('google-play', 'app-store', 'dev-test')), 'providers whose purchases this server verifies; empty = the products are only shown, nothing can be bought'),
  products: arrayOf(STORE_PRODUCT),
  obfuscatedAccountId: described(str, 'pass to the store with the purchase (Play: setObfuscatedAccountId)'),
});
const AD_PLACEMENT: S = oneOfValues('ad_battle_double', 'ad_grave_extra', 'ad_darts_free', 'ad_daily_gift_x2', 'ad_rest');
const AD_PLACEMENT_STATUS: S = obj({ placement: AD_PLACEMENT, enabled: bool, dailyCap: int, usedToday: int, remainingToday: int, maxAmount: described(nullable(int), 'the largest reward of one view (ad_battle_double: gold)') });
export const AD_TICKET: S = obj({
  ticketId: str,
  placement: AD_PLACEMENT,
  provider: oneOfValues('admob', 'dev-test'),
  status: oneOfValues('open', 'granted', 'failed', 'expired'),
  createdAt: ts,
  expiresAt: ts,
  ssv: described(obj({ userId: str, customData: str }), 'server-side verification options for the ad SDK'),
  result: described(nullable(openObject('the granted reward; fields depend on the placement')), 'null until granted'),
  failure: nullable(str),
  grantedAt: nullable(ts),
});
const PLAY_GAMES_STATUS: S = obj({ available: bool, linked: bool });
const EMAIL_STATUS: S = obj({
  available: described(bool, "the server sends e-mails (feature 'email')"),
  email: described(nullable(str), 'the confirmed sign-in address (null: none)'),
  pendingEmail: described(nullable(str), 'an address waiting for its confirmation link (null: none)'),
});
const INTEGRITY_RESULT: S = obj({
  verdict: oneOfValues('ok', 'suspicious', 'failed'),
  checks: obj({ app: bool, device: bool, licensed: bool, fresh: bool }),
  at: ts,
});
const PUSH_DEVICES: S = obj({ devices: described(int, 'push devices registered for the account') });

export const RESULT_SCHEMAS: { readonly [K in RequestType]: S } = {
  'system.hello': obj({
    protocol: int,
    serverVersion: str,
    minClientVersion: nullable(str),
    features: arrayOf(oneOfValues(...ALL_FEATURES)),
    limits: SERVER_LIMITS,
    serverTime: ts,
    publicBaseUrl: nullable(str),
  }),
  'system.time': obj({ serverTime: ts }),
  'system.describe': openObject('this API description document'),
  'events.poll': EVENT_BATCH,
  'events.resume': EVENT_BATCH,

  'auth.guest': ISSUED_AUTH_RESULT,
  'auth.signUp': SIGNED_IN_RESULT,
  'auth.signIn': SIGNED_IN_RESULT,
  'auth.session': obj({ session: CURRENT_SESSION_INFO, player: PLAYER_CARD }),
  'auth.register': ISSUED_AUTH_RESULT,
  'auth.login': ISSUED_AUTH_RESULT,
  'auth.resume': AUTH_RESULT,
  'auth.link': ISSUED_AUTH_RESULT,
  'auth.refresh': ISSUED_AUTH_RESULT,
  'auth.sessions': obj({ sessions: arrayOf(AUTH_SESSION_INFO) }),
  'auth.revoke': obj({ revoked: int }),
  'auth.logout': obj({ revoked: int }),
  'auth.playGames': SIGNED_IN_RESULT,
  'auth.linkPlayGames': PLAY_GAMES_STATUS,
  'auth.unlinkPlayGames': PLAY_GAMES_STATUS,
  'auth.playGamesStatus': PLAY_GAMES_STATUS,
  'auth.emailSignUp': SIGNED_IN_RESULT,
  'auth.emailSignIn': SIGNED_IN_RESULT,
  'auth.emailStatus': EMAIL_STATUS,
  'auth.emailChange': EMAIL_STATUS,
  'auth.emailResend': EMAIL_STATUS,
  'auth.emailVerify': obj({ verified: oneOfValues(true), email: described(str, 'the confirmed address, partly hidden') }),
  'auth.passwordResetRequest': obj({ accepted: oneOfValues(true) }),
  'auth.passwordReset': obj({ reset: oneOfValues(true) }),
  'integrity.challenge': obj({ challenge: described(str, 'pass it as the requestHash of the Play Integrity token request'), expiresAt: ts }),
  'integrity.verify': INTEGRITY_RESULT,
  'push.register': PUSH_DEVICES,
  'push.unregister': PUSH_DEVICES,
  'push.test': obj({ sent: int }),
  'account.delete': obj({ playerId: described(str, 'the deleted account'), deletedAt: ts }),

  'profile.load': obj({ profile: PLAYER_PROFILE, created: bool, serverTime: ts, pendingGrants: int, volatile: PROFILE_VOLATILE }, ['volatile']),
  'profile.save': obj({ revision: int, updatedAt: ts, serverTime: ts, summary: PROFILE_SUMMARY }),
  'profile.me': PLAYER_CARD,
  'profile.setDisplayName': obj({ displayName: str }),
  'profile.getPublic': obj({ profile: PUBLIC_PROFILE }),
  'profile.getPublicBatch': obj({ profiles: arrayOf(PUBLIC_PROFILE) }),
  'profile.importLegacy': obj({ revision: int, legacyImport: LEGACY_IMPORT, summary: PROFILE_SUMMARY }),
  'profile.listGrants': obj({ grants: arrayOf(GRANT), items: arrayOf(GRANT), next: cursorNext }),

  'player.command': PLAYER_COMMAND_RESULT,

  'leaderboard.get': LEADERBOARD_PAGE,

  'assault.getZones': obj({ serverTime: ts, zones: arrayOf(ASSAULT_ZONE) }),
  'assault.getTargets': obj({ serverTime: ts, targets: arrayOf(ASSAULT_TARGET) }),
  'assault.report': obj({ zone: ASSAULT_ZONE, captured: bool, serverTime: ts }),
  'assault.claimTribute': TRIBUTE_RESULT,
  'clan.list': obj({ clans: arrayOf(CLAN_SUMMARY), items: arrayOf(CLAN_SUMMARY), next: cursorNext }),
  'clan.get': obj({ clan: CLAN_INFO }),
  'clan.mine': CLAN_MINE,
  'clan.members': obj({ clanId: int, members: arrayOf(CLAN_MEMBER), items: arrayOf(CLAN_MEMBER), next: cursorNext }),
  'clan.create': obj({ clan: CLAN_INFO }),
  'clan.update': obj({ clan: CLAN_INFO }),
  'clan.join': obj({ clanId: int, role: CLAN_ROLE }),
  'clan.request': obj({ request: CLAN_JOIN_REQUEST }),
  'clan.cancelRequest': EMPTY,
  'clan.requests': obj({ items: arrayOf(CLAN_JOIN_REQUEST), next: cursorNext }),
  'clan.decide': obj({ clan: CLAN_INFO }, ['clan'], 'the clan card after an accepted request; nothing after a declined one'),
  'clan.kick': EMPTY,
  'clan.setRole': EMPTY,
  'clan.transfer': EMPTY,
  'clan.leave': obj({ clanId: { type: 'null' }, previousClanId: nullable(int), disbanded: bool }),
  'clan.disband': EMPTY,
  'clan.history': obj({ items: arrayOf(CLAN_EVENT), next: cursorNext }),

  'chat.channels': obj({ channels: arrayOf(CHAT_CHANNEL_INFO), mutedUntil: nullable(ts) }),
  'chat.history': obj({ channel: CHAT_CHANNEL, items: arrayOf(CHAT_MESSAGE), next: cursorNext }),
  'chat.send': obj({ message: CHAT_MESSAGE }),
  'chat.subscribe': obj({ subscribed: arrayOf(CHAT_CHANNEL) }),
  'chat.unsubscribe': obj({ subscribed: arrayOf(CHAT_CHANNEL) }),
  'chat.markRead': obj({ channel: CHAT_CHANNEL, readSeq: int, unread: int }),
  'auction.get': AUCTION_INFO,

  'store.catalog': STORE_CATALOG,
  'ads.status': obj({ providers: arrayOf(oneOfValues('admob', 'dev-test')), placements: arrayOf(AD_PLACEMENT_STATUS), dailyCapTotal: int, remainingToday: int, resetsAt: ts }),
  'ads.start': obj({ ticket: AD_TICKET }),
  'ads.ticket': obj({ ticket: AD_TICKET }),
  'ads.testReward': obj({ ticket: AD_TICKET }),

  'news.list': obj({ items: arrayOf(NEWS_ITEM), next: cursorNext }),

  'replay.save': obj({ replayId: str }),
  'replay.list': obj({ items: arrayOf(REPLAY_SUMMARY), next: cursorNext }),
  'replay.get': obj({ replay: REPLAY_RECORD }),

  'matchmaking.join': obj({ ticket: MATCH_TICKET }),
  'matchmaking.leave': EMPTY,
  'room.list': obj({ rooms: arrayOf(ROOM), items: arrayOf(ROOM), next: cursorNext }),
  'room.get': roomResult,
  'room.create': roomResult,
  'room.join': roomResult,
  'room.leave': EMPTY,
  'room.update': roomResult,
  'room.setReady': roomResult,
  'room.setTeam': roomResult,
  'room.chat': EMPTY,
  'room.start': sessionResult,

  'session.start': sessionResult,
  'session.get': obj({
    session: SESSION_INFO,
    log: COMMAND_LOG_SCHEMA,
    turns: arrayOf(SESSION_TURN_SUMMARY),
    hashes: arrayOf(SESSION_CHECKPOINT),
    snapshotTick: described(
      { type: 'null' },
      'reserved for state snapshots; always null in this server version: a client rebuilds the battle from init + seed + the whole log (deterministic re-simulation) and checks `hashes`',
    ),
  }),
  'session.input': obj({ throughTick: int }),
  'session.endTurn': obj({ turn: int, team: int, startTick: int, deadlineAt: nullable(ts) }),
  'session.hash': obj({ match: nullable(bool) }),
  'session.finish': obj({ ended: bool, outcome: nullable(SESSION_OUTCOME), status: oneOfValues('waiting', 'verifying', 'ended') }),
  'session.forfeit': EMPTY,
  'session.dispute': obj({ status: oneOfValues('resolving', 'ended'), outcome: nullable(SESSION_OUTCOME) }),
};

// ------------------------------------------------------------------------------------------------ events

/** JSON Schema of the `payload` of every server event. */
export const EVENT_PAYLOAD_SCHEMAS: { readonly [E in EventType]: S } = {
  'profile.changed': obj({ revision: int, updatedAt: ts }),
  'grant.received': obj({ grant: GRANT }),
  'assault.zoneChanged': obj({ zone: ASSAULT_ZONE }),
  'auction.updated': obj({ auction: AUCTION_STATE }),
  'matchmaking.matched': obj({ ticketId: str, roomId: str }),
  'room.updated': obj({ room: ROOM }),
  'room.closed': obj({ roomId: str, reason: oneOfValues('empty', 'expired', 'operator'), version: int }),
  'room.chat': obj({ roomId: str, playerId: str, displayName: str, text: str, at: ts, version: int }),
  'session.started': obj({ session: SESSION_INFO }),
  'session.input': obj({ sessionId: str, playerId: str, team: int, turn: int, fromTick: int, throughTick: int, commands: arrayOf(INPUT_COMMAND_SCHEMA), version: int }),
  'session.turn': obj({
    sessionId: str,
    endedTurn: int,
    endTick: int,
    turn: int,
    team: nullable(int),
    startTick: nullable(int),
    forced: bool,
    deadlineAt: nullable(ts),
    version: int,
  }),
  'session.desync': obj({ sessionId: str, tick: int, hashes: mapOf(str, 'playerId -> reported hash'), version: int }),
  'session.ended': obj({ sessionId: str, outcome: SESSION_OUTCOME, version: int }),
  'clan.updated': obj({ clan: CLAN_INFO }),
  'clan.membership': obj({
    clanId: nullable(int),
    role: nullable(CLAN_ROLE),
    reason: oneOfValues('created', 'joined', 'accepted', 'left', 'kicked', 'disbanded', 'role', 'leader', 'declined'),
  }),
  'clan.requestsChanged': obj({ clanId: int, pending: int }),
  'chat.message': obj({ message: CHAT_MESSAGE }),
  'chat.deleted': obj({ channel: CHAT_CHANNEL, messageIds: arrayOf(str), reason: oneOfValues(...CHAT_DELETE_REASONS) }),
};
